/**
 * Owner-rule omen markers (O-025, 2026-07-19).
 *
 * The three marker slots above a dungeon entrance represent the SPECIFIC enemies the
 * primed plan will field, per the owner's rule:
 *
 *   - one-boss dungeons  → the TWO most common ordinary enemies + the boss;
 *   - two-boss dungeons  → the single most common ordinary enemy + BOTH bosses;
 *   - when ordinary enemies tie on planned count at a needed rank, pick RANDOMLY
 *     among the tied ids — seeded by the plan fingerprint so every observer of the
 *     same primed cycle sees the same panel (the §8 multiplayer invariant).
 *
 * This complements (does not replace) the broad-family `deriveDungeonEnemyPreview`:
 * the family preview remains the accessible-text summary; these markers drive the
 * three physical icon slots. Additive module — no existing contract changes.
 */
import { SeededRandom } from './random.js';
import { DungeonEntranceError, } from './entrance-types.js';
import { validateDungeonEnemyIconRules } from './entrance-validate.js';
function titleize(id) {
    return id
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
/** Family icon for a specific enemy — same scoring as the family preview so art stays consistent. */
function familyRuleFor(enemy, rules) {
    const fallback = rules.find((rule) => rule.fallback);
    const scored = rules
        .filter((rule) => !rule.fallback)
        .map((rule) => ({
        rule,
        score: rule.tags.filter((tag) => enemy.tags.includes(tag)).length * 3 +
            rule.roles.filter((role) => enemy.roles.includes(role)).length * 2,
    }))
        .filter((entry) => entry.score > 0)
        .sort((left, right) => right.score - left.score ||
        right.rule.priority - left.rule.priority ||
        left.rule.id.localeCompare(right.rule.id));
    return scored[0]?.rule ?? fallback;
}
/**
 * Derives the three owner-rule marker slots from the actual generated plan.
 * Deterministic for a given plan (ties broken by a fingerprint-seeded PRNG).
 * May return fewer than three markers only when the plan itself fields fewer
 * distinct enemies than slots — the renderer shrouds unfilled slots.
 */
export function deriveDungeonOmenMarkers(plan, catalog, iconRules) {
    const ruleProblems = validateDungeonEnemyIconRules(iconRules);
    if (ruleProblems.length > 0)
        throw new DungeonEntranceError(ruleProblems);
    const enemyById = new Map(catalog.enemies.map((enemy) => [enemy.id, enemy]));
    const ordinaryCounts = new Map();
    const bossIds = [];
    const missing = new Set();
    for (const room of plan.rooms) {
        if (!room.encounter)
            continue;
        for (const member of room.encounter.members) {
            const enemy = enemyById.get(member.enemyId);
            if (!enemy) {
                missing.add(member.enemyId);
                continue;
            }
            const isBoss = room.encounter.kind === 'boss' || enemy.boss === true;
            if (isBoss) {
                if (!bossIds.includes(member.enemyId))
                    bossIds.push(member.enemyId);
            }
            else {
                ordinaryCounts.set(member.enemyId, (ordinaryCounts.get(member.enemyId) ?? 0) + member.count);
            }
        }
    }
    if (missing.size > 0) {
        throw new DungeonEntranceError([...missing].sort().map((enemyId) => ({
            code: 'omen-unknown-enemy',
            path: 'plan.rooms.encounter.members',
            message: `unknown enemy '${enemyId}'`,
        })));
    }
    // Deterministic tie-break: same plan → same panel for every observer.
    const random = new SeededRandom(`omen-markers::${plan.fingerprint}`);
    const commonSlotCount = bossIds.length >= 2 ? 1 : 2;
    const pickedCommon = [];
    const remaining = new Map(ordinaryCounts);
    while (pickedCommon.length < commonSlotCount && remaining.size > 0) {
        const topCount = Math.max(...remaining.values());
        const tied = [...remaining.entries()]
            .filter(([, count]) => count === topCount)
            .map(([id]) => id)
            .sort(); // stable candidate order BEFORE the seeded pick
        const chosen = tied.length === 1 ? tied[0] : random.pick(tied);
        pickedCommon.push(chosen);
        remaining.delete(chosen);
    }
    const markerFor = (enemyId, isBoss, slot) => {
        const enemy = enemyById.get(enemyId);
        const rule = familyRuleFor(enemy, iconRules);
        return {
            slot,
            enemyId,
            label: enemy.label ?? titleize(enemyId),
            iconId: rule.id,
            symbolKey: rule.symbolKey,
            preparationHint: rule.preparationHint,
            isBoss,
            plannedCount: isBoss ? 1 : (ordinaryCounts.get(enemyId) ?? 0),
        };
    };
    // Common enemies fill from the left; bosses anchor the right-hand slots.
    const markers = [];
    for (const id of pickedCommon)
        markers.push(markerFor(id, false, markers.length + 1));
    for (const id of bossIds.slice(0, 3 - markers.length > 0 ? 3 - markers.length : 0))
        markers.push(markerFor(id, true, markers.length + 1));
    // If boss slots remain unfilled (catalogue edge), backfill with further ordinary ranks.
    while (markers.length < 3 && remaining.size > 0) {
        const topCount = Math.max(...remaining.values());
        const tied = [...remaining.entries()]
            .filter(([, count]) => count === topCount)
            .map(([id]) => id)
            .sort();
        const chosen = tied.length === 1 ? tied[0] : random.pick(tied);
        remaining.delete(chosen);
        markers.push(markerFor(chosen, false, markers.length + 1));
    }
    return markers;
}
//# sourceMappingURL=omen-markers.js.map